import { globalVariables } from "helper/globalVariables";
import Dropdown from "shared/Components/dropdown";
import { SearchLocation } from "shared/Components/searchLocation";
import SearchBar from "shared/Components/search";
import { useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import { TableWithPagination } from "shared/Components/tableWithPagination";
import { globalClasses } from "helper/globalClasses";
import { JudgeListMobileTable } from "./JudgeListMobileTable";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  UPDATE_JUDGE_STATUS_GQL,
  getJudgeListQuery,
} from "redux/features/hostSlice/judgeSlice/judgeGql";
import useEffectAfterMount from "helper/useEffectAfterMount";
import { UPDATE_TRIAL_APPROVAL_MUTATION } from "redux/features/trialCalendar/trialCalendarGql";
import Loader from "shared/Components/loader";
import { PopUpMessages } from "shared/Components/popUpMessages";
import { MultiSelectChipsWithSearchDropdown } from "shared/Components/multiSelectWithSearchAndChips";
import { useMediaQuery } from "react-responsive";
import PageInfo from "shared/Components/pageInfo";
import { Button } from "shared/Components/button";

export const APPROVE_JUDGE = "Approved Judges";
export const APPROVE_OR_REJECT_JUDGE = "Approve/Reject Judges";

export const VIEW_JUDGE_TABS = [APPROVE_JUDGE, APPROVE_OR_REJECT_JUDGE];

const noviceDropdownData = [
  globalVariables.novice,
  globalVariables.advanced,
  globalVariables.excellent,
  globalVariables.premier,
  globalVariables.provisional,
];

const headData = [
  globalVariables.judgeName,
  globalVariables.phoneNumber,
  globalVariables.email,
  globalVariables.state,
  globalVariables.city,
  globalVariables.zip,
  globalVariables.level,
  globalVariables.distanceReadyToTravel,
  globalVariables.status,
];

export const PendingJudges = ({children}) => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [levelFilter, setLevelFilter] = useState([]);
  const [stateFilter, setStateFilter] = useState(null);
  const [cityFilter, setCityFilter] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loadingRowId, setLoadingRowId] = useState(null);

  const options = noviceDropdownData.map((data, index) => ({
    label: data,
    value: `option_${index}`,
  }));

  const handleFilterChange = (selectedOptions) => {
    const selectedValues = selectedOptions.map((option) => option.label);
    setLevelFilter(selectedValues);
  };

  const { paginatedDataCls } = globalClasses;
  const [getData, { loading, error, data, refetch }] = useLazyQuery(
    getJudgeListQuery,
    {
      fetchPolicy: "network-only",
    }
  );

  const popUpModalRef = useRef();

  const [apiData, setApiData] = useState({
    title: "",
    desc: "",
    type: "pending",
  });

  const [updateJudgeStatus] = useMutation(UPDATE_JUDGE_STATUS_GQL);

  const resultData = data?.getJudgeList;
  const judges = resultData?.data || [];

  const handlePaginationData = (page = 1) => {
    setPage(page);
  };

  useEffect(() => {
    getData({
      variables: {
        pageNumber: page,
        pageSize: 10,
        level: levelFilter.length === 0 ? null : levelFilter,
        city: cityFilter,
        state: stateFilter,
        search: search,
        judgeStatus: "active",
      },
    });
  }, [page]);

  useEffectAfterMount(() => {
    setPage(1);
    getData({
      variables: {
        pageNumber: 1,
        pageSize: 10,
        level: levelFilter.length === 0 ? null : levelFilter,
        state: stateFilter,
        city: cityFilter,
        search: search,
        judgeStatus: "active",
      },
    });
  }, [levelFilter, cityFilter, stateFilter, search]);

  const onChangeStatus = async (value, id) => {
    try {
      setLoadingRowId(id);
      const { data } = await updateJudgeStatus({
        variables: {
          authId: id,
          judgeStatus: value,
        },
      });
      setApiData({
        title: "Success",
        desc: data.updateJudgeStatus.message,
        type: "success",
      });
      await refetch();
      popUpModalRef.current.openModal();
      setLoadingRowId(null);
    } catch (error) {
      setLoadingRowId(null);
      setApiData({
        title: "error",
        desc: error.message,
        type: "pending",
      });
    }
  };
  const capitalizeFirstCharacter = (str) =>
    str.charAt(0).toUpperCase() + str.slice(1);

  const renderButton = (judgeStatus, id) => {
    console.log("AUth ID ", id);
    return (
      <>
        {loadingRowId === id ? (
          <Loader />
        ) : (
          <div className="flex justify-center">
            <Dropdown
              defaultOption={capitalizeFirstCharacter(judgeStatus) || "Select"}
              width={"w-[150px]"}
              mainContent="w-100%"
              dropdownOptions={["Approve", "Reject"]}
              onChange={(option) =>
                onChangeStatus(
                  option == "Approve" ? "approved" : "rejected",
                  id
                )
              }
            />
          </div>
        )}
      </>
    );
  };

  const renderTableBody = () => {
    return judges.map((judge, index) => (
      <tr key={judge.name}>
        <td className={paginatedDataCls(index)}>{judge.name ?? "-"}</td>
        <td className={paginatedDataCls(index)}>{judge.phone_number ?? "-"}</td>
        <td className={paginatedDataCls(index)}>{judge.email ?? "-"}</td>
        <td className={paginatedDataCls(index)}>{judge.state ?? "-"}</td>
        <td className={paginatedDataCls(index)}>{judge.city ?? "-"}</td>
        <td className={paginatedDataCls(index)}>{judge.zip ?? "-"}</td>
        <td className={paginatedDataCls(index)}>
          {judge.level?.length
            ? judge.level?.length > 1
              ? judge.level.join(",")
              : judge.level
            : "-"}
        </td>
        <td className={paginatedDataCls(index)}>
          {judge.distance_travel ?? "-"}
        </td>
        <td className={paginatedDataCls(index)}>
          {renderButton(judge.judgeStatus, judge.authId)}
        </td>
      </tr>
    ));
  };

  const onSearch = (event) => {
    const value = event?.target?.value;
    setSearch(value);
  };

  const onCloseModal = () => {
    if (apiData.type === "success") {
      handlePaginationData();
    }
  };

  // const handlePaginationData = (pages = 1) => {
  //   setPage(pages);
  //   onChange(pages, "pageNumber");
  // };

  const debouncedSearch = debounce(onSearch, 1000);

  const onClearFilters = () => {
    setCityFilter(null);
    setStateFilter(null);
    setLevelFilter([]);
    setSearch("");
  };

  return (
    <>
      <PageInfo  title={globalVariables.viewJudges}>
        <Button
          width={"w-auto"}
          label="Clear All Filters"
          onClick={onClearFilters} 
        />
      </PageInfo>
      <div ref={popUpModalRef} className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 my-5 order-3">
        <SearchBar
          defaultValue={search}
          isShadow={true}
          width={"w-full"}
          onChange={debouncedSearch}
          placeholder={"Name"}
          isFaded={true}
          />
        <SearchLocation
          isShadow={true}
          width={"w-full"}
          name="state"
          placeholder={globalVariables.state}
          types={["(states)"]}
          onSelectLocation={(location) => {
            setStateFilter(location);
          }}
          defaultValue={stateFilter}
        />
        <SearchLocation
          isShadow={true}
          width={"w-full"}
          name="city"
          placeholder={globalVariables.city}
          types={["(cities)"]}
          onSelectLocation={(location) => {
            setCityFilter(location);
          }}
          searchBy={stateFilter}
          defaultValue={cityFilter}
        />
        <MultiSelectChipsWithSearchDropdown
          isShadow={true}
          width={"w-full"}
          placeholder={globalVariables.level}
          options={options}
          label=""
          onChange={handleFilterChange}
          defaultSelectedOptions={levelFilter}
        />
      </div>
      <div className="order-4">
        {isMobile ? (
          <JudgeListMobileTable
            judges={judges}
            currentPage={page}
            totalPages={resultData?.totalPages}
            loading={loading}
            handlePageChange={(page) => handlePaginationData(page)}
          />
        ) : (
          <TableWithPagination
            headData={headData}
            tableBody={renderTableBody()}
            itemsPerPage={10}
            tableHeight={"md:h-[calc(100vh-10rem)] lg:h-[100vh]"}
            totalPage={resultData?.totalPages}
            handlePageChange={(page) => handlePaginationData(page)}
            loading={loading}
            current={page}
            persistPagination={true}
            error={error}
          />
        )}
      </div>
      <PopUpMessages
        title={apiData.title}
        desc={apiData.desc}
        type={apiData.type}
        ref={popUpModalRef}
        onCloseModal={onCloseModal}
      />
    </>
  );
};
